import React, { useEffect, useRef, useState } from 'react';
import api, { formatApiError } from '../api/client';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, Search, X, CheckCircle2, LogIn, LogOut, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { StatusBadge } from './Dashboard';

// Three kinds of pass can be presented at the gate, mirroring the mobile scanner:
//   gate     — GP-…  or its 6-digit passcode   → /gate-passes
//   monthly  — MP-…  or its 6-digit passcode   → /monthly-passes
//   visitor  — VGP-… (QR code only)             → /visitor-gate-passes
const KIND_LABEL = { gate: 'Gate Pass', monthly: 'Monthly Pass', visitor: 'Visitor Pass' };

// Read the real backend message (NestJS returns `message`, not `detail`).
const errorText = (e) => formatApiError(e?.response?.data?.detail, e?.response?.data);

const todayIST = () => new Date().toLocaleString('sv-SE', { timeZone: 'Asia/Kolkata' }).slice(0, 10);
const fmt = (iso) => (iso ? format(new Date(iso), 'dd MMM HH:mm') : '—');

export default function Scanner() {
  const [code, setCode] = useState('');
  const [direction, setDirection] = useState('out');
  const [pass, setPass] = useState(null);
  const [kind, setKind] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [cameraOn, setCameraOn] = useState(false);
  const scannerRef = useRef(null);
  const elemId = 'qr-reader';

  const startCamera = async () => {
    setErr('');
    try {
      const qr = new Html5Qrcode(elemId);
      scannerRef.current = qr;
      setCameraOn(true);
      await qr.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        (decoded) => {
          qr.stop().then(() => { setCameraOn(false); }).catch(() => {});
          setCode(decoded);
          lookup(decoded);
        },
        () => {}
      );
    } catch (e) {
      setCameraOn(false);
      setErr('Camera not accessible. Use passcode below.');
    }
  };

  const stopCamera = async () => {
    try {
      if (scannerRef.current) await scannerRef.current.stop();
    } catch {}
    setCameraOn(false);
  };

  useEffect(() => () => { stopCamera(); }, []);

  const show = (data, k) => {
    setPass(data);
    setKind(k);
    // A visitor's direction is implied by their state: already inside → OUT, otherwise → IN.
    if (k === 'visitor') setDirection(data.visitor?.status === 'checked_in' ? 'out' : 'in');
  };

  const lookup = async (val) => {
    // Pass IDs are always uppercase (A–Z, 0–9); passcodes are digits, so uppercasing is safe.
    const q = (val ?? code).trim().toUpperCase();
    if (!q) return;
    setErr(''); setPass(null); setKind(null); setScanResult(null); setLoading(true);
    try {
      if (q.startsWith('VGP-')) {
        const { data } = await api.get('/visitor-gate-passes/find', { params: { code: q } });
        return show(data, 'visitor');
      }
      if (q.startsWith('MP-')) {
        const { data } = await api.get('/monthly-passes/find', { params: { code: q } });
        return show(data, 'monthly');
      }
      try {
        const { data } = await api.get(`/gate-passes/${encodeURIComponent(q)}`);
        return show(data, 'gate');
      } catch (e) {
        // A 6-digit passcode may belong to a monthly pass instead — fall through on 404 only.
        if (e?.response?.status !== 404 || q.startsWith('GP-')) throw e;
      }
      try {
        const { data } = await api.get('/monthly-passes/find', { params: { code: q } });
        return show(data, 'monthly');
      } catch (e) {
        if (e?.response?.status === 404) {
          setErr('No gate pass or monthly pass matches this code.');
          return;
        }
        throw e;
      }
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setLoading(false);
    }
  };

  const doScan = async () => {
    if (!pass) return;
    setLoading(true);
    try {
      if (kind === 'gate') {
        const { data } = await api.post('/gate-passes/scan', {
          pass_id_or_code: pass.pass_id,
          direction,
          remarks,
        });
        toast.success(direction === 'out' ? `${data.employee_name} marked OUT` : `${data.employee_name} marked IN`);
        setPass(data);
        setRemarks('');
      } else if (kind === 'monthly') {
        const { data } = await api.post('/monthly-passes/scan', {
          pass_id_or_code: pass.pass_id,
          direction,
        });
        toast.success(`${data.employee_name}: ${data.message}`);
        setScanResult(data);
      } else if (kind === 'visitor') {
        const { data } = await api.post('/visitor-gate-passes/scan', {
          pass_id_or_code: pass.qr_code,
          direction,
        });
        toast.success(data.message);
        setPass(data);
      }
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setCode(''); setPass(null); setKind(null); setScanResult(null); setErr(''); setRemarks('');
  };

  // Whether the Confirm button should be offered. The backend re-validates every scan.
  const canConfirm = (() => {
    if (!pass) return false;
    if (kind === 'gate') {
      return (direction === 'out' && pass.status === 'approved') || (direction === 'in' && pass.status === 'out');
    }
    if (kind === 'monthly') {
      const t = todayIST();
      return pass.active && t >= pass.valid_from && t <= pass.valid_until;
    }
    if (kind === 'visitor') {
      const v = pass.visitor;
      if (direction === 'in') {
        return pass.status === 'active' && v?.request_status === 'approved' && new Date() <= new Date(pass.valid_until);
      }
      return v?.status === 'checked_in';
    }
    return false;
  })();

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="scanner-page">
      <header>
        <div className="overline">Guard Console</div>
        <h1 className="heading text-3xl font-black text-ink">QR Code Scanner</h1>
        <p className="text-sm text-zinc-500 mt-1">Scan or enter a gate pass, monthly pass, or visitor pass.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button className={`btn py-3 ${direction === 'out' ? 'bg-primary text-white' : 'bg-white border border-zinc-300 text-zinc-700'}`} onClick={() => setDirection('out')} data-testid="dir-out">
          <LogOut size={16} /> Mark Exit (OUT)
        </button>
        <button className={`btn py-3 ${direction === 'in' ? 'bg-green-600 text-white' : 'bg-white border border-zinc-300 text-zinc-700'}`} onClick={() => setDirection('in')} data-testid="dir-in">
          <LogIn size={16} /> Mark Return (IN)
        </button>
      </div>

      <div className="card p-4">
        <div className="overline mb-2">Option 1 · Camera scanner</div>
        <div className="relative w-full aspect-square max-w-md mx-auto bg-zinc-100 border-2 border-dashed border-zinc-300 rounded-sm overflow-hidden">
          {/* html5-qrcode owns this element and replaces its children with the video feed.
              React must never render children into it, or unmounting them crashes
              ("removeChild: the node to be removed is not a child of this node"). */}
          <div id={elemId} className="w-full h-full" />
          {!cameraOn && (
            <div className="absolute inset-0 flex items-center justify-center text-zinc-400 text-center pointer-events-none">
              <div><Camera size={40} className="mx-auto mb-2" /><div className="text-sm">Camera is off</div></div>
            </div>
          )}
        </div>
        <div className="mt-3 flex justify-center gap-2">
          {!cameraOn ? (
            <button className="btn-primary" onClick={startCamera} data-testid="start-camera"><Camera size={14} /> Enable Camera</button>
          ) : (
            <button className="btn-outline" onClick={stopCamera} data-testid="stop-camera"><X size={14} /> Stop Camera</button>
          )}
        </div>
      </div>

      <div className="card p-4">
        <div className="overline mb-2">Option 2 · Enter Pass ID or 6-digit passcode</div>
        <div className="flex gap-2">
          <input
            className="input font-mono"
            placeholder="e.g. 483921, GP-…, MP-… or VGP-…"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && lookup()}
            data-testid="manual-code"
          />
          <button className="btn-primary" onClick={() => lookup()} disabled={loading || !code} data-testid="lookup-btn">
            <Search size={14} /> Lookup
          </button>
        </div>
      </div>

      {err && (
        <div className="card p-4 border-red-300 bg-red-50 flex items-center gap-3">
          <AlertTriangle className="text-red-600" />
          <div><div className="font-bold text-red-800">{err}</div></div>
        </div>
      )}

      {pass && (
        <div className="card p-6 shadow-hard" data-testid="pass-result">
          {kind === 'gate' && <GatePassDetails pass={pass} />}
          {kind === 'monthly' && <MonthlyPassDetails pass={pass} scanResult={scanResult} />}
          {kind === 'visitor' && <VisitorPassDetails pass={pass} />}

          {canConfirm && (
            <div className="mt-4 space-y-2">
              {kind === 'gate' && (
                <>
                  <label className="label">Guard Remarks</label>
                  <input className="input" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="optional remarks" data-testid="guard-remarks" />
                </>
              )}
              <button className="btn-primary w-full py-3" onClick={doScan} disabled={loading} data-testid="confirm-scan">
                <CheckCircle2 size={16} />{' '}
                {kind === 'visitor'
                  ? `Confirm Visitor ${direction === 'out' ? 'CHECK-OUT' : 'CHECK-IN'}`
                  : `Confirm ${direction === 'out' ? 'EXIT' : (kind === 'monthly' ? 'ENTRY' : 'RETURN')}`}
              </button>
            </div>
          )}

          <button className="btn-ghost mt-3 w-full" onClick={reset} data-testid="reset-scan">Scan another</button>
        </div>
      )}
    </div>
  );
}

function PassHeader({ kind, title, subtitle, badge }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div>
        <div className="overline">{KIND_LABEL[kind]}</div>
        <div className="heading font-black text-2xl text-ink">{title}</div>
        <div className="text-xs text-zinc-500 font-mono">{subtitle}</div>
      </div>
      {badge}
    </div>
  );
}

function Notice({ tone, children }) {
  const cls = tone === 'red'
    ? 'bg-red-50 border-red-300 text-red-800'
    : tone === 'green'
    ? 'bg-green-50 border-green-300 text-green-800'
    : 'bg-yellow-50 border-yellow-300 text-yellow-800';
  return <div className={`mt-4 p-3 border rounded-sm text-sm ${cls}`}>{children}</div>;
}

function GatePassDetails({ pass }) {
  return (
    <>
      <PassHeader kind="gate" title={pass.employee_name} subtitle={pass.pass_id} badge={<StatusBadge status={pass.status} />} />
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><span className="overline block">Department</span>{pass.department}</div>
        <div><span className="overline block">Type</span>{pass.pass_type}</div>
        <div className="col-span-2"><span className="overline block">Reason</span>{pass.reason}</div>
        <div><span className="overline block">Approved By</span>{pass.approved_by_name || '—'}</div>
        <div><span className="overline block">Requested</span>{fmt(pass.created_at)}</div>
        {pass.time_out && <div><span className="overline block">Out At</span>{fmt(pass.time_out)}</div>}
        {pass.time_in && <div><span className="overline block">In At</span>{fmt(pass.time_in)}</div>}
      </div>
      {pass.status === 'pending' && (
        <Notice tone="yellow"><AlertTriangle size={14} className="inline mr-1" /> This pass is still pending supervisor approval — do not allow exit.</Notice>
      )}
      {pass.status === 'rejected' && <Notice tone="red">Rejected: {pass.rejected_reason || '—'}</Notice>}
    </>
  );
}

function MonthlyPassDetails({ pass, scanResult }) {
  const t = todayIST();
  return (
    <>
      <PassHeader
        kind="monthly"
        title={pass.employee_name}
        subtitle={pass.pass_id}
        badge={<span className={pass.active ? 'badge-success' : 'badge-danger'}>{pass.active ? 'Active' : 'Inactive'}</span>}
      />
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><span className="overline block">Employee Code</span>{pass.employee_code || '—'}</div>
        <div><span className="overline block">Department</span>{pass.department || '—'}</div>
        <div><span className="overline block">Valid From</span>{pass.valid_from}</div>
        <div><span className="overline block">Valid Until</span>{pass.valid_until}</div>
        <div><span className="overline block">Issued By</span>{pass.issued_by_name || '—'}</div>
      </div>
      {!pass.active && <Notice tone="red">Inactive{pass.deactivated_reason ? `: ${pass.deactivated_reason}` : ''} — do not allow entry or exit.</Notice>}
      {pass.active && t > pass.valid_until && <Notice tone="red">This pass expired on {pass.valid_until}.</Notice>}
      {pass.active && t < pass.valid_from && <Notice tone="yellow">This pass is not valid until {pass.valid_from}.</Notice>}
      {scanResult && (
        <Notice tone="green">
          {scanResult.message} · now <strong>{scanResult.location_status}</strong>
          {scanResult.auto_action && <div className="mt-1 text-xs">{scanResult.auto_action}</div>}
        </Notice>
      )}
    </>
  );
}

const VISITOR_STATUS = {
  active: { label: 'Active', cls: 'badge-info' },
  used: { label: 'Used', cls: 'badge-muted' },
  expired: { label: 'Expired', cls: 'badge-danger' },
};

function VisitorPassDetails({ pass }) {
  const v = pass.visitor || {};
  const s = VISITOR_STATUS[pass.status] || { label: pass.status, cls: 'badge-muted' };
  const lapsed = pass.status === 'active' && new Date() > new Date(pass.valid_until);
  return (
    <>
      <PassHeader kind="visitor" title={pass.visitor_name} subtitle={pass.qr_code} badge={<span className={s.cls}>{s.label}</span>} />
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><span className="overline block">Phone</span>{pass.visitor_phone || '—'}</div>
        <div><span className="overline block">Type</span>{v.visitor_type || '—'}</div>
        <div><span className="overline block">To Meet</span>{v.to_meet || '—'}</div>
        <div><span className="overline block">Vehicle</span>{v.vehicle_number || '—'}</div>
        <div className="col-span-2"><span className="overline block">Purpose</span>{v.purpose || '—'}</div>
        <div><span className="overline block">Valid Until</span>{fmt(pass.valid_until)}</div>
        <div><span className="overline block">Visitor Status</span>{v.status ? v.status.replace('_', ' ') : 'not arrived'}</div>
        {v.check_in && <div><span className="overline block">Checked In</span>{fmt(v.check_in)}</div>}
        {v.check_out && <div><span className="overline block">Checked Out</span>{fmt(v.check_out)}</div>}
      </div>
      {v.request_status && v.request_status !== 'approved' && (
        <Notice tone="red">Visitor request is {v.request_status} — do not allow entry.</Notice>
      )}
      {(pass.status === 'expired' || lapsed) && v.status !== 'checked_in' && (
        <Notice tone="red">This visitor pass has expired.</Notice>
      )}
      {v.status === 'checked_out' && <Notice tone="yellow">This visitor has already checked out.</Notice>}
    </>
  );
}
